import store from "@/store/index"
import { setErrorMessage, handleFetchError } from './FetchErrorHandler'
import { getLevel } from '@/utils/chat/chatUtil';
import { getToken, getUser } from '@/utils/auth'
// ★ 公共分片解析器：与 localStream / resume-stream 完全同口径
// （offset 一致性 + 半截分片安全），主窗口不再自建切分逻辑
import { parseChunkBuffer } from '@/utils/localStream';
import { nanoid } from 'nanoid';

export const CONTENT_TYPE_JSON = "application/json;charset=utf-8";

export async function fetchAndProcessData(url, data, handleDataFunction) {
    let readTimeout = null
    let responseChunk = ''
    let { question, aiContent, params } = data

    let options = { method: 'POST', signal: params.signal }
    options.headers = new Headers({
        "Content-Type": CONTENT_TYPE_JSON,
        "Authorization": getToken(),
        "username": getUser() && getUser().phoneNumber
    })

    let level = "";
    const withOutLevelUrls = ["/Composition"];
    if (question && question.level) {
        level = question.level
    }
    if (!level) {
        level = getLevel()
    }
    if (!withOutLevelUrls.some(target => url.includes(target))) {
        const { signal, ...bodyParams } = params;
        options.body = JSON.stringify({ ...bodyParams, level });
    }

    // ★ 复用同一 decoder：跨 chunk 保留多字节解码状态
    const decoder = new TextDecoder('utf-8');

    try {
        const response = await fetch(url, options)
        if (!response.ok) {
            console.log(response.status)
            setErrorMessage(response.status || 3, aiContent)
            return;   // ★ 错误态直接返回，不再对错误响应继续读流
        }
        const reader = response.body.getReader()

        async function readChunk() {
            const { done, value } = await reader.read()
            if (done) {
                console.log('全部数据接收完毕')

                // ★ 收尾：处理末尾可能没有换行的最后一条记录
                if (responseChunk) {
                    const { parsed } = parseChunkBuffer(responseChunk, true);
                    if (parsed) {
                        for (const json of parsed) {
                            if (json && json.hb) {
                                if (aiContent) aiContent._lastHb = Date.now();
                                continue;
                            }
                            if (json && json.msg === 'stream-start') continue;
                            handleDataFunction(json, aiContent, question);
                            if (aiContent) {
                                aiContent._receivedCount = (aiContent._receivedCount || 0) + 1;
                            }
                        }
                    }
                    responseChunk = '';
                }

                if (aiContent) {
                    aiContent.isDone = 1
                    if (aiContent.msg === "loading") {
                        aiContent.msg = ""
                    }
                    // ★ sessionStorage 同步桥：流结束后写一次最终 offset
                    try {
                        sessionStorage.setItem(
                            `resume:${aiContent.params?.requestId || aiContent.id}`,
                            String(aiContent._receivedCount || 0)
                        );
                    } catch (e) {}
                    await store.dispatch('updateMessageToConversation', { message: aiContent });
                }
                clearTimeout(readTimeout)
                if (question) {
                    question.isLoading = false
                }
                reader.releaseLock()
                return
            }
            clearTimeout(readTimeout)
            // ★ stream: true 防止多字节字符被切在 chunk 边界时产出替换字符
            responseChunk += decoder.decode(value, { stream: true })

            // ─────────────────────────────────────────────
            // ★ 公共解析器解析：按 '\n' 增量切分，完整行立即可喂渲染
            // ─────────────────────────────────────────────
            const { parsed, rest } = parseChunkBuffer(responseChunk);
            if (parsed === null) {
                return readChunk();               // 半截分片，等下一块再拼
            }
            responseChunk = rest;

            for (const json of parsed) {
                // ★ 心跳帧：不喂渲染、不计 offset，只刷活跃时间
                if (json && json.hb) {
                    if (aiContent) aiContent._lastHb = Date.now();
                    continue;
                }
                if (json && json.msg === 'stream-start') continue;
                handleDataFunction(json, aiContent, question);
                // ★ offset 计数（与后端 entry.tokens 严格一一对应）
                if (aiContent) {
                    aiContent._receivedCount = (aiContent._receivedCount || 0) + 1;
                }
            }

            // ★ sessionStorage 同步桥：每块只写一次，避免逐条 I/O
            //   防 DB offset 滞后导致刷新后回放重复
            if (aiContent) {
                try {
                    sessionStorage.setItem(
                        `resume:${aiContent.params?.requestId || aiContent.id}`,
                        String(aiContent._receivedCount || 0)
                    );
                } catch (e) {}
            }

            return readChunk()
        }
        // 开始读取数据块
        readChunk()
    } catch (error) {
        if (aiContent?._aborted) {
            clearTimeout(readTimeout);
            return;
        }
        handleFetchError(error, aiContent, question)
    } finally {
        clearTimeout(readTimeout)
    }
}

export function handleChatParams(data) {
    let { aiContent } = data;
    let params = aiContent.params;
  
    // ★ requestId 出生时已由 createAutoMsg 分配，这里只做两件事：
    // 1) 被主动取消过 → 换新 id（旧 id 的后端缓存已销毁）
    // 2) 兜底：非 createAutoMsg 出生、且没有 id 的极老数据
    if (!aiContent.id) {
      aiContent.id = nanoid(6);
      aiContent.requestId = aiContent.id;
    } else if (aiContent._aborted) {
      aiContent.id = nanoid(6);
      aiContent.requestId = aiContent.id;
    }
    // 普通重试（无 _aborted）：复用原 id/requestId，账本沿用，续传语义正确
  
    aiContent._receivedCount = 0;
    aiContent._aborted = false;
    aiContent._background = false;
  
    params.requestId = aiContent.requestId || aiContent.id;
  
    // ★ controller 残留修复
    if (!aiContent.controller || aiContent.controller.signal.aborted) {
      aiContent.controller = new AbortController();
    }
    params.signal = aiContent.controller.signal;
    params.query = params.text;
    data.params = params;
    // ★ 刷新恢复的凭证落库：此刻 requestId 已就位，必须写进 IndexedDB，
    // 否则刷新后 loadAllConversations 读回的快照没有 requestId，无法续传
    // （JSON.stringify 会自动剥离 signal/controller 等不可序列化字段，落库安全；
    //   fire-and-forget，不阻塞请求发起）
    if (aiContent.sessionId) {
      store.dispatch('updateMessageToConversation', { message: aiContent })
        .catch(() => {});
    }
    return data;
  }



export function handleQuestionParams(data) {
    let params = { ...data }
    if (params.question) delete params.question
    data.params = params
    return data;
}

export function hanldeWallParams(data, knowledge) {
    return {
        aiContent: data,
        params: {
            knowledge,
            signal: data.controller.signal,
        }
    }
}

export function handleSimilarParams(data) {
    data.params = {
        id: data.question.id,
        signal: data.aiContent?.controller?.signal
    }
    return data
}
